import { DoctorEntry, DoctorMedia, DoctorPublication } from '../types'
import { buildPerson, compact, JsonLdGraph, JsonLdNode } from './graph'
import { organizationId, personId, ref } from './ids'

export const buildDoctorPerson = (doctor: DoctorEntry): JsonLdNode =>
  compact({
    ...buildPerson(doctor),
    description: doctor.bio,
    alumniOf: doctor.alumniOf?.map((name) => ({ '@type': 'EducationalOrganization', name })),
    worksFor: ref(organizationId()),
    subjectOf: doctor.media?.map((_, index) => ref(doctorMediaId(doctor, index)))
  })

const publicationId = (doctor: DoctorEntry, index: number): string =>
  `${personId(doctor.slug)}-publication-${index + 1}`
const doctorMediaId = (doctor: DoctorEntry, index: number): string => `${personId(doctor.slug)}-media-${index + 1}`

export const buildDoctorPublication = (
  doctor: DoctorEntry,
  publication: DoctorPublication,
  index: number
): JsonLdNode =>
  compact({
    '@type': 'ScholarlyArticle',
    '@id': publicationId(doctor, index),
    headline: publication.title,
    author: ref(personId(doctor.slug)),
    datePublished: publication.year,
    isPartOf: { '@type': 'Periodical', name: publication.journal },
    url: publication.url,
    sameAs: publication.url
  })

export const buildDoctorMedia = (doctor: DoctorEntry, media: DoctorMedia, index: number): JsonLdNode =>
  compact({
    '@type': 'NewsArticle',
    '@id': doctorMediaId(doctor, index),
    headline: media.headline ?? media.text,
    url: media.url,
    datePublished: media.datePublished,
    publisher: { '@type': 'Organization', name: media.publisher }
  })

/** Enrich the existing ProfilePage graph using exactly the records displayed by the doctor template. */
export const withDoctorGraph = (graph: JsonLdGraph, doctor: DoctorEntry): JsonLdGraph => ({
  ...graph,
  '@graph': [
    ...graph['@graph'].map((node) => (node['@id'] === personId(doctor.slug) ? buildDoctorPerson(doctor) : node)),
    ...(doctor.publications ?? []).map((publication, index) => buildDoctorPublication(doctor, publication, index)),
    ...(doctor.media ?? []).map((media, index) => buildDoctorMedia(doctor, media, index))
  ]
})
